/**
 * Tracker — GTM, campaign persistence, and internal pageview tracking.
 *
 * Ported from the legacy `Tracker` class in `apps/web/src/utils/tracking.ts`.
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { clientLogger } from '~/lib/logger';
import type { PluginInitContext } from '~/domain/plugins/types';
import {
  initGtm,
  pushToDataLayer,
} from '~/domain/plugins/services/tracker/gtm.client';
import { env } from '~/platform/platform';
import {
  initCampaign,
  getCampaign as getCampaignData,
  type CampaignData,
} from '~/domain/plugins/services/tracker/persist-campaign.client';
import { trackPageview } from '~/domain/plugins/services/tracker/holdbar-custom.client';
import type {
  TrackerEventName,
  TrackerEventPayloads,
} from '~/domain/plugins/services/tracker/events';
import { getCookieConsent as getCookieConsentFromPixels } from '~/domain/plugins/services/pixels.client';
import { usePluginsReady } from '../plugins-ready';

const MEASUREMENT_ID_FIELD = 'measurement_id';
const GA_FALLBACK_MEASUREMENT_ID = 'G-5ND4ECDKQB';

let initialized = false;
let companyId: string | undefined;
let measurementId: string | undefined;

function isPreviewMode(): boolean {
  try {
    return window.location.hostname === 'localhost';
  } catch {
    return false;
  }
}

export async function init(ctx: PluginInitContext) {
  try {
    initCampaign();

    const { marketingProviders } = ctx;

    // Our container always runs: it is the one that turns the Backoffice
    // tracking settings (GA4 measurement id, the Google Ads conversion we
    // manage) into tags. A customer's own container runs next to it rather
    // than instead of it. Loading theirs *instead* left the conversion action
    // we set up with nothing reading its id and label, while the setup check
    // read as passed (US-12819).
    const ownContainerId = isPreviewMode()
      ? env.GTM_CONTAINER_ID_PREVIEW
      : env.GTM_CONTAINER_ID;
    const containerIds = [
      ownContainerId,
      marketingProviders?.containerId,
    ].filter((id): id is string => Boolean(id));

    const params: Record<string, string> = {};
    if (marketingProviders) {
      for (const [k, v] of Object.entries(marketingProviders)) {
        // Container params are the providers' strings (ids, labels, domains).
        // A switch such as `includeUserData` is read on the server instead.
        if (typeof v === 'string' && v && k !== 'containerId') {
          params[k] = v;
        }
      }
    }

    params[MEASUREMENT_ID_FIELD] =
      marketingProviders?.measurementId ?? GA_FALLBACK_MEASUREMENT_ID;
    measurementId = params[MEASUREMENT_ID_FIELD];

    if (params['conversionLinkerDomains']) {
      params['conversionLinkerDomains'] = [
        params['conversionLinkerDomains'],
        window.location.hostname,
      ].join(',');
    } else {
      params['conversionLinkerDomains'] = window.location.hostname;
    }

    companyId = ctx.companyId;

    initGtm({
      containerIds,
      nonce: ctx.nonce,
      params,
      companyId: ctx.companyId,
    });

    // The documented dataLayer contract is `company_id` only — internal
    // identifiers don't belong in a container the customer controls.
    pushToDataLayer({
      company_id: ctx.companyId,
    });

    initialized = true;
  } catch (error) {
    clientLogger.warn(error, '[tracker] Failed to initialize');
  }
}

export function pageview(
  path: string,
  locale: string,
  payload?: Record<string, unknown>
): void {
  try {
    const campaign = getCampaign();

    pushToDataLayer({
      event: 'page_view',
      ...(payload ?? {}),
      ...(campaign ?? {}),
    });

    trackPageview({ path, locale, companyId });
  } catch (error) {
    clientLogger.warn(error, '[tracker] Failed to track pageview');
  }
}

export function event<Name extends TrackerEventName>(
  eventName: Name,
  payload: TrackerEventPayloads[Name]
): void {
  try {
    const campaign = getCampaign();

    pushToDataLayer({
      event: eventName,
      ...(payload ?? {}),
      ...(campaign ?? {}),
    });
  } catch (error) {
    clientLogger.warn(error, '[tracker] Failed to track event');
  }
}

export function getCampaign(): CampaignData | undefined {
  try {
    return getCampaignData();
  } catch {
    return undefined;
  }
}

export function getCookieConsent(): Record<string, string> | undefined {
  return getCookieConsentFromPixels();
}

export function getMeasurementId(): string | undefined {
  return measurementId;
}

export function isReady(): boolean {
  return initialized;
}

/**
 * Tracks pageviews on route changes.
 * Render this component inside the tree after tracker init.
 */
export function PageviewTracker({ locale }: { locale: string }) {
  const location = useLocation();
  const ready = usePluginsReady();

  useEffect(() => {
    if (ready) {
      pageview(location.pathname, locale);
    }
  }, [location.pathname, locale, ready]);

  return null;
}
